import { useState, useEffect, useCallback, useRef } from 'react';
import { preferencesAPI } from '../services/api';

/**
 * Custom hook to sync user preferences with backend database
 * Falls back to localStorage if offline or API fails
 * 
 * HYBRID APPROACH:
 * 1. On mount: Load from database → fallback to localStorage if error
 * 2. On change: Save to localStorage immediately (fast, offline support)
 *               Save to database in background (cross-device sync)
 * 3. Handles errors gracefully, maintains offline functionality
 * 
 * @param {string} preferenceKey - Key in preferences object (e.g., 'customerFilters')
 * @param {any} defaultValue - Default value if no preference exists
 * @returns {object} { value, setValue, reset, isLoading, isSyncing, error }
 */
export function useSyncedPreferences(preferenceKey, defaultValue) {
  const [value, setValue] = useState(defaultValue);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState(null);
  
  // Use ref to track if component is mounted (prevent state updates after unmount)
  const isMounted = useRef(true);
  
  // Debounce timer for database sync
  const syncTimerRef = useRef(null);

  // Load preferences from backend on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Try to load from backend database
        const response = await preferencesAPI.get();
        const serverPrefs = (response && response.data && response.data.preferences) ? response.data.preferences : {};
        const savedValue = serverPrefs[preferenceKey];

        if (!isMounted.current) return;

        if (savedValue !== undefined && savedValue !== null) {
          // Database has the preference
          setValue(savedValue);
          // Also save to localStorage as backup/cache
          try {
            localStorage.setItem(preferenceKey, JSON.stringify(savedValue));
          } catch (localError) {
            console.warn('Failed to cache to localStorage:', localError);
          }
        } else {
          // No server preference, try localStorage as fallback
          const localValue = localStorage.getItem(preferenceKey);
          if (localValue) {
            try {
              const parsedValue = JSON.parse(localValue);
              setValue(parsedValue);
              
              // Sync localStorage value to database
              try {
                await preferencesAPI.update({
                  [preferenceKey]: parsedValue
                });
              } catch (syncError) {
                console.warn('Failed to sync localStorage to database:', syncError);
              }
            } catch (parseError) {
              console.error('Failed to parse localStorage value:', parseError);
              setValue(defaultValue);
            }
          } else {
            // No saved preference anywhere, use default
            setValue(defaultValue);
          }
        }
      } catch (err) {
        console.error('Failed to load preferences from server:', err);
        
        if (!isMounted.current) return;
        
        setError(err?.response?.data?.error?.message || err.message || 'Failed to load preferences');

        // Fallback to localStorage when backend fails
        try {
          const localValue = localStorage.getItem(preferenceKey);
          if (localValue) {
            setValue(JSON.parse(localValue));
          } else {
            setValue(defaultValue);
          }
        } catch (localError) {
          console.error('Failed to load from localStorage:', localError);
          setValue(defaultValue);
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    loadPreferences();

    // Cleanup function
    return () => {
      isMounted.current = false;
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }
    };
  }, [preferenceKey, defaultValue]);

  // Save preference to backend (debounced to avoid too many requests)
  const updatePreference = useCallback(
    (newValue) => {
      // Update state immediately for responsive UI
      setValue(newValue);

      // Save to localStorage immediately (optimistic update, works offline)
      try {
        localStorage.setItem(preferenceKey, JSON.stringify(newValue));
      } catch (localError) {
        console.warn('Failed to save to localStorage:', localError);
      }

      // Clear existing sync timer
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
      }

      // Debounce database sync (wait 500ms after last change)
      syncTimerRef.current = setTimeout(async () => {
        if (!isMounted.current) return;

        setIsSyncing(true);
        try {
          await preferencesAPI.update({
            [preferenceKey]: newValue
          });
          
          if (isMounted.current) {
            setError(null);
          }
        } catch (err) {
          console.error('Failed to sync preferences to server:', err);
          
          if (isMounted.current) {
            setError(err.message || 'Failed to sync preferences');
          }
          
          // localStorage still has the value as fallback
          // User can continue working offline
        } finally {
          if (isMounted.current) {
            setIsSyncing(false);
          }
        }
      }, 500); // 500ms debounce
    },
    [preferenceKey]
  );

  // Reset to default value
  const reset = useCallback(async () => {
    setValue(defaultValue);
    
    // Clear from localStorage
    try {
      localStorage.removeItem(preferenceKey);
    } catch (localError) {
      console.warn('Failed to clear localStorage:', localError);
    }

    // Clear from database
    setIsSyncing(true);
    try {
      await preferencesAPI.update({
        [preferenceKey]: defaultValue
      });
      
      if (isMounted.current) {
        setError(null);
      }
    } catch (err) {
      console.error('Failed to reset preferences on server:', err);
      
      if (isMounted.current) {
        setError(err.message || 'Failed to reset preferences');
      }
    } finally {
      if (isMounted.current) {
        setIsSyncing(false);
      }
    }
  }, [preferenceKey, defaultValue]);

  return {
    value,
    setValue: updatePreference,
    reset,
    isLoading,
    isSyncing,
    error
  };
}

export default useSyncedPreferences;
